// Client Deliverables page - for is_client=1 users to view and manage their deliverables
import { useState, useEffect } from 'react';
import {
  Package,
  Loader2,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  MessageSquare,
  ChevronDown,
  X,
  Send,
  FileIcon,
  Video,
  Globe,
  Eye,
  HelpCircle,
  RotateCcw
} from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import api from '@/utils/api';
import { createClientQuery, getClientQueries, reopenClientQuery, ClientQuery, getQueryComments, addQueryComment, QueryComment, getClientQueryCategories, uploadQueryAttachments, QueryCategoryOption, QueryAttachment } from '@/actions/client-portal';
import { AttachmentList, FilePicker } from '@/components/queries/QueryAttachments';

// Helper: convert URLs in text to clickable links
const linkifyText = (text: string) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand underline hover:text-brand-hover break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

interface DeliverableComment {
  id: number;
  user_name: string;
  comment: string;
  created_at: string;
}

interface BlogStatus {
  is_blog: boolean;
  has_url?: boolean;
  blog_status?: 'draft' | 'published' | null;
  blog_title?: string;
  published_at?: string;
  error?: string;
}

interface Deliverable {
  id: number;
  uuid: string;
  title: string;
  description: string;
  type: 'blog' | 'video';
  url?: string;
  status: 'approved' | 'pending' | 'needs_revision';
  delivery_month?: number;
  delivery_year?: number;
  created_at: string;
  reviewer_name?: string;
  reviewed_at?: string;
  review_token?: string;
  unread_comment_count?: number;
}

interface DeliverableStats {
  total: number;
  pending: number;
  approved: number;
  needs_revision: number;
}

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 3 }, (_, i) => currentYear - i);

const ClientDeliverables = () => {
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [stats, setStats] = useState<DeliverableStats>({ total: 0, pending: 0, approved: 0, needs_revision: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterMonth, setFilterMonth] = useState<number | null>(null);
  const [filterYear, setFilterYear] = useState<number | null>(null);

  // Comment state
  const [expandedDeliverable, setExpandedDeliverable] = useState<number | null>(null);
  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [deliverableComments, setDeliverableComments] = useState<DeliverableComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Blog status state
  const [blogStatuses, setBlogStatuses] = useState<{ [key: number]: BlogStatus }>({});

  // Query modal state
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [querySubject, setQuerySubject] = useState('');
  const [queryMessage, setQueryMessage] = useState('');
  const [queryCategoryId, setQueryCategoryId] = useState<number | ''>('');
  const [queryFiles, setQueryFiles] = useState<File[]>([]);
  const [queryCategories, setQueryCategories] = useState<QueryCategoryOption[]>([]);
  const [submittingQuery, setSubmittingQuery] = useState(false);

  // Queries list state
  const [activeTab, setActiveTab] = useState<'deliverables' | 'queries'>('deliverables');
  const [queries, setQueries] = useState<ClientQuery[]>([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [queryStats, setQueryStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });

  // Reopen query state
  const [queryToReopen, setQueryToReopen] = useState<ClientQuery | null>(null);
  const [reopenComment, setReopenComment] = useState('');
  const [reopeningQuery, setReopeningQuery] = useState(false);

  // Detail modal state
  const [selectedDeliverable, setSelectedDeliverable] = useState<Deliverable | null>(null);

  // Query comments state
  const [expandedQuery, setExpandedQuery] = useState<number | null>(null);
  const [queryComments, setQueryComments] = useState<QueryComment[]>([]);
  const [loadingQueryComments, setLoadingQueryComments] = useState(false);
  const [queryCommentText, setQueryCommentText] = useState('');
  const [queryCommentFiles, setQueryCommentFiles] = useState<File[]>([]);
  const [addingQueryComment, setAddingQueryComment] = useState(false);

  // Fetch deliverables
  const fetchDeliverables = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.append('month', String(filterMonth));
      if (filterYear) params.append('year', String(filterYear));

      const response = await api.get<{
        deliverables: Deliverable[];
        stats: DeliverableStats
      }>(`/client-portal/deliverables?${params.toString()}`);

      const fetchedDeliverables = response?.data?.deliverables || [];
      setDeliverables(fetchedDeliverables);
      setStats(response?.data?.stats || { total: 0, pending: 0, approved: 0, needs_revision: 0 });

      // Fetch blog status for blog-type deliverables
      const blogDeliverables = fetchedDeliverables.filter(d => d.type === 'blog');
      if (blogDeliverables.length > 0) {
        const statusPromises = blogDeliverables.map(async (d) => {
          try {
            const statusResponse = await api.get<BlogStatus>(`/delivery-hub/deliverables/${d.id}/blog-status`);
            return { id: d.id, status: statusResponse?.data || { is_blog: false } };
          } catch {
            return { id: d.id, status: { is_blog: false } };
          }
        });
        const statuses = await Promise.all(statusPromises);
        const statusMap: { [key: number]: BlogStatus } = {};
        statuses.forEach(s => { statusMap[s.id] = s.status; });
        setBlogStatuses(statusMap);
      }
    } catch (error) {
      console.error('Error fetching deliverables:', error);
      showToast('Failed to load deliverables', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch queries
  const fetchQueries = async () => {
    setLoadingQueries(true);
    try {
      const result = await getClientQueries();
      setQueries(result.queries);
      setQueryStats(result.stats);
    } catch (error) {
      console.error('Error fetching queries:', error);
    } finally {
      setLoadingQueries(false);
    }
  };

  useEffect(() => {
    fetchDeliverables();
    fetchQueries();
  }, [filterMonth, filterYear]);

  useEffect(() => {
    getClientQueryCategories().then(setQueryCategories);
  }, []);

  const resetQueryForm = () => {
    setShowQueryModal(false);
    setQuerySubject('');
    setQueryMessage('');
    setQueryCategoryId('');
    setQueryFiles([]);
  };

  /** Upload picked files; returns null (and shows an error) if the upload failed */
  const uploadFiles = async (files: File[]): Promise<QueryAttachment[] | null> => {
    if (files.length === 0) return [];
    const upload = await uploadQueryAttachments(files);
    if (!upload.success) {
      showToast(upload.message || 'Failed to upload files', 'error');
      return null;
    }
    return upload.attachments;
  };

  // Fetch comments for a deliverable
  const fetchComments = async (deliverableId: number) => {
    setLoadingComments(true);
    try {
      const response = await api.get<{ comments: DeliverableComment[] }>(
        `/client-portal/deliverables/${deliverableId}/comments`
      );
      setDeliverableComments(response?.data?.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  // Add comment
  const handleAddComment = async () => {
    if (!commentText.trim() || !expandedDeliverable) return;

    setAddingComment(true);
    try {
      const response = await api.post<{ comment: DeliverableComment }>(
        `/client-portal/deliverables/${expandedDeliverable}/comments`,
        { comment: commentText }
      );

      if (response?.data?.comment) {
        setDeliverableComments(prev => [response.data.comment, ...prev]);
        setCommentText('');
        showToast('Comment added', 'success');

        // Client's own comment won't be unread (backend updates client_read_at)
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      showToast('Failed to add comment', 'error');
    } finally {
      setAddingComment(false);
    }
  };

  // Submit a new query
  const handleSubmitQuery = async () => {
    if (!queryCategoryId || !querySubject.trim() || !queryMessage.trim()) {
      showToast('Please choose a category and fill in the title and description', 'error');
      return;
    }

    setSubmittingQuery(true);
    try {
      const attachments = await uploadFiles(queryFiles);
      if (!attachments) return;
      const result = await createClientQuery({
        category_id: Number(queryCategoryId),
        subject: querySubject.trim(),
        message: queryMessage.trim(),
        attachments,
      });
      if (result.success) {
        showToast('Query submitted successfully', 'success');
        resetQueryForm();
        // Refresh queries list and switch to queries tab
        fetchQueries();
        setActiveTab('queries');
      } else {
        showToast(result.message || 'Failed to submit query', 'error');
      }
    } catch (error) {
      console.error('Error submitting query:', error);
      showToast('Failed to submit query', 'error');
    } finally {
      setSubmittingQuery(false);
    }
  };

  // Reopen a resolved query
  const handleReopenQuery = async () => {
    if (!reopenComment.trim() || !queryToReopen) {
      showToast('Please provide a reason for reopening', 'error');
      return;
    }

    setReopeningQuery(true);
    try {
      const result = await reopenClientQuery(queryToReopen.id, reopenComment.trim());
      if (result.success) {
        showToast('Query reopened successfully', 'success');
        setQueryToReopen(null);
        setReopenComment('');
        // Refresh queries list
        fetchQueries();
      } else {
        showToast(result.message || 'Failed to reopen query', 'error');
      }
    } catch (error) {
      console.error('Error reopening query:', error);
      showToast('Failed to reopen query', 'error');
    } finally {
      setReopeningQuery(false);
    }
  };

  // Fetch comments for a query
  const fetchQueryComments = async (queryId: number) => {
    setLoadingQueryComments(true);
    try {
      const result = await getQueryComments(queryId);
      setQueryComments(result.comments);
    } catch (error) {
      console.error('Error fetching query comments:', error);
    } finally {
      setLoadingQueryComments(false);
    }
  };

  // Toggle query comments expansion
  const toggleQueryComments = (queryId: number) => {
    setQueryCommentFiles([]);
    if (expandedQuery === queryId) {
      setExpandedQuery(null);
      setQueryComments([]);
    } else {
      setExpandedQuery(queryId);
      fetchQueryComments(queryId);
    }
  };

  // Add comment to query
  const handleAddQueryComment = async () => {
    if ((!queryCommentText.trim() && queryCommentFiles.length === 0) || !expandedQuery) return;

    setAddingQueryComment(true);
    try {
      const attachments = await uploadFiles(queryCommentFiles);
      if (!attachments) return;
      const result = await addQueryComment(expandedQuery, queryCommentText.trim(), attachments);
      if (result.success && result.comment) {
        setQueryComments(prev => [...prev, result.comment!]);
        setQueryCommentText('');
        setQueryCommentFiles([]);
        showToast('Comment added', 'success');
      } else {
        showToast(result.message || 'Failed to add comment', 'error');
      }
    } catch (error) {
      console.error('Error adding query comment:', error);
      showToast('Failed to add comment', 'error');
    } finally {
      setAddingQueryComment(false);
    }
  };

  // Get query status badge
  const getQueryStatusBadge = (status: string) => {
    switch (status) {
      case 'resolved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
            <CheckCircle size={12} />
            Resolved
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
            <Clock size={12} />
            In Progress
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
            <Clock size={12} />
            Open
          </span>
        );
    }
  };

  const clearFilters = () => {
    setFilterMonth(null);
    setFilterYear(null);
  };

  const hasFilters = filterMonth !== null || filterYear !== null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
            <CheckCircle size={12} />
            Approved
          </span>
        );
      case 'needs_revision':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
            <AlertCircle size={12} />
            Needs Revision
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
            <Clock size={12} />
            Pending
          </span>
        );
    }
  };

  const getTypeIcon = (type: string) => {
    if (type === 'blog') {
      return <FileIcon size={20} className="text-brand" />;
    }
    return <Video size={20} className="text-pink-600" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getReviewUrl = (deliverable: Deliverable) => {
    if (deliverable.review_token) {
      return `${window.location.origin}/review/${deliverable.review_token}`;
    }
    return deliverable.url;
  };

  // Blog deliverable urls are stored as bare slugs (e.g. "some-post-slug"),
  // not full URLs, so they need the blog domain prefixed to be openable.
  const getContentUrl = (deliverable: Deliverable) => {
    if (!deliverable.url) return deliverable.url;
    if (deliverable.type === 'blog' && !/^https?:\/\//i.test(deliverable.url)) {
      return `https://yellowslate.com/blog/${deliverable.url}`;
    }
    return deliverable.url;
  };

  const getBlogStatusBadge = (deliverable: Deliverable) => {
    const blogStatus = blogStatuses[deliverable.id];
    if (!blogStatus || !blogStatus.is_blog) return null;

    if (blogStatus.blog_status === 'published') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          <Globe size={12} />
          Published
        </span>
      );
    } else if (blogStatus.blog_status === 'draft') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          <Clock size={12} />
          Draft
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center">
              <Package size={24} className="text-brand" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Your Deliverables</h1>
              <p className="text-gray-500 text-sm">Review and approve content created for your school</p>
            </div>
          </div>

          {/* Filters and Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <select
                value={filterMonth || ''}
                onChange={(e) => setFilterMonth(e.target.value ? Number(e.target.value) : null)}
                className="appearance-none bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-brand cursor-pointer"
              >
                <option value="">All Months</option>
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={filterYear || ''}
                onChange={(e) => setFilterYear(e.target.value ? Number(e.target.value) : null)}
                className="appearance-none bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-brand cursor-pointer"
              >
                <option value="">All Years</option>
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={14} />
                Clear
              </button>
            )}

            <div className="w-px h-6 bg-gray-200 mx-1 hidden md:block" />

            <button
              onClick={() => setShowQueryModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors text-sm font-medium"
            >
              <HelpCircle size={16} />
              Raise a Query
            </button>
          </div>
        </div>
      </div>

      {/* Stats - Compact */}
      <div className="bg-white border-b px-6 py-2">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5">
            <Clock size={14} className="text-amber-500" />
            <span className="font-semibold text-gray-900">{stats.pending}</span>
            <span className="text-gray-500">Pending</span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <CheckCircle size={14} className="text-green-500" />
            <span className="font-semibold text-gray-900">{stats.approved}</span>
            <span className="text-gray-500">Approved</span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <AlertCircle size={14} className="text-red-500" />
            <span className="font-semibold text-gray-900">{stats.needs_revision}</span>
            <span className="text-gray-500">Revision</span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-gray-900">{stats.total}</span>
            <span className="text-gray-500">Total</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b px-6">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('deliverables')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'deliverables'
                ? 'border-brand text-brand'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Package size={16} />
              Deliverables
              <span className="px-1.5 py-0.5 text-xs bg-gray-100 rounded-full">{stats.total}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('queries')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'queries'
                ? 'border-brand text-brand'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <HelpCircle size={16} />
              Your Queries
              {queryStats.open > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">{queryStats.open} open</span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50">
        {activeTab === 'deliverables' ? (
          // Deliverables Tab Content
          deliverables.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <Package size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {hasFilters ? 'No deliverables found' : 'No deliverables yet'}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm">
              {hasFilters
                ? 'Try adjusting your filters to see more results.'
                : 'When your YellowSlate team creates content for your school, it will appear here for your review and approval.'
              }
            </p>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 text-sm text-brand hover:underline"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deliverables.map((deliverable) => (
              <div
                key={deliverable.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  setSelectedDeliverable(deliverable);
                  setExpandedDeliverable(deliverable.id);
                  fetchComments(deliverable.id);
                }}
              >
                {/* Card Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center border">
                      {getTypeIcon(deliverable.type)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {getStatusBadge(deliverable.status)}
                      {deliverable.type === 'blog' && getBlogStatusBadge(deliverable)}
                    </div>
                  </div>

                  {/* Card Content */}
                  <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">
                    {deliverable.title}
                  </h3>
                  {deliverable.delivery_month && deliverable.delivery_year && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                      <Calendar size={12} />
                      <span>{monthNames[deliverable.delivery_month - 1]} {deliverable.delivery_year}</span>
                    </div>
                  )}
                  {deliverable.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                      {deliverable.description}
                    </p>
                  )}

                  {/* Approval Info */}
                  {deliverable.status === 'approved' && deliverable.reviewer_name && (
                    <p className="text-xs text-green-600 flex items-center gap-1 mb-3">
                      <CheckCircle size={12} />
                      Approved by {deliverable.reviewer_name.split('[')[0].trim()}
                    </p>
                  )}

                  {/* Card Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      {formatDate(deliverable.created_at)}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDeliverable(deliverable);
                          setExpandedDeliverable(deliverable.id);
                          fetchComments(deliverable.id);
                        }}
                        className={`relative transition-colors ${
                          expandedDeliverable === deliverable.id
                            ? 'text-brand'
                            : 'text-gray-400 hover:text-brand'
                        }`}
                        title="Comments"
                      >
                        <MessageSquare size={14} />
                        {(deliverable.unread_comment_count ?? 0) > 0 && (
                          <span className="absolute -top-2 -right-2 min-w-[16px] h-4 flex items-center justify-center px-1 text-[10px] font-bold text-white bg-amber-500 rounded-full">
                            {deliverable.unread_comment_count}
                          </span>
                        )}
                      </button>
                      {/* Show View Content only if published (not draft) */}
                      {deliverable.url && (
                        deliverable.type !== 'blog' || blogStatuses[deliverable.id]?.blog_status === 'published'
                      ) && (
                        <a
                          href={getContentUrl(deliverable)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-400 hover:text-brand transition-colors"
                          title="View content"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      {/* Show Review link for pending items OR draft blogs */}
                      {deliverable.review_token && (
                        deliverable.status === 'pending' ||
                        (deliverable.type === 'blog' && blogStatuses[deliverable.id]?.blog_status === 'draft')
                      ) && (
                        <a
                          href={getReviewUrl(deliverable)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-brand text-white rounded hover:bg-brand-hover transition-colors"
                          title="Review & Approve"
                        >
                          <Eye size={12} />
                          Review
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
        ) : (
          // Queries Tab Content
          loadingQueries ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 size={32} className="animate-spin text-brand" />
            </div>
          ) : queries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <HelpCircle size={32} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No queries yet</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Have a question or need help? Click "Raise a Query" to submit your question.
              </p>
              <button
                onClick={() => setShowQueryModal(true)}
                className="mt-4 flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors text-sm font-medium"
              >
                <HelpCircle size={16} />
                Raise a Query
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {queries.map((query) => (
                <div
                  key={query.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">{query.subject}</h3>
                        <p className="text-sm text-gray-500">
                          {query.category && (
                            <span className="inline-block mr-2 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                              {query.category.name}
                            </span>
                          )}
                          Submitted on {new Date(query.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          {query.created_by_name && ` by ${query.created_by_name}`}
                        </p>
                      </div>
                      {getQueryStatusBadge(query.status)}
                    </div>

                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{query.message}</p>
                    <div className="mb-3">
                      <AttachmentList attachments={query.attachments} />
                    </div>

                    {query.status === 'resolved' && query.resolved_by_name && (
                      <p className="text-xs text-green-600 flex items-center gap-1 mb-3">
                        <CheckCircle size={12} />
                        Resolved by {query.resolved_by_name}
                        {query.resolved_at && ` on ${new Date(query.resolved_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}`}
                      </p>
                    )}

                    {/* Actions Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <button
                        onClick={() => toggleQueryComments(query.id)}
                        className={`flex items-center gap-1.5 text-sm transition-colors ${
                          expandedQuery === query.id
                            ? 'text-brand'
                            : 'text-gray-500 hover:text-brand'
                        }`}
                      >
                        <MessageSquare size={14} />
                        View Conversation
                      </button>
                      {query.status === 'resolved' && (
                        <button
                          onClick={() => setQueryToReopen(query)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        >
                          <RotateCcw size={14} />
                          Reopen
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Comments Section */}
                  {expandedQuery === query.id && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Conversation</h4>

                      {/* Comments Thread */}
                      {loadingQueryComments ? (
                        <div className="flex justify-center py-4">
                          <Loader2 size={20} className="animate-spin text-gray-400" />
                        </div>
                      ) : queryComments.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No messages yet. Start the conversation below.</p>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                          {queryComments.map((comment) => (
                            <div
                              key={comment.id}
                              className={`rounded-lg p-3 border ${
                                comment.user_type === 'admin'
                                  ? 'bg-blue-50 border-blue-100 ml-4'
                                  : 'bg-white border-gray-200 mr-4'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-sm font-medium ${
                                  comment.user_type === 'admin' ? 'text-blue-700' : 'text-gray-900'
                                }`}>
                                  {comment.user_name}
                                  {comment.user_type === 'admin' && (
                                    <span className="ml-1 text-xs font-normal text-blue-500">(YellowSlate)</span>
                                  )}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {formatDate(comment.created_at)}
                                </span>
                              </div>
                              {comment.comment && (
                                <p className={`text-sm ${
                                  comment.user_type === 'admin' ? 'text-blue-800' : 'text-gray-700'
                                }`}>{linkifyText(comment.comment)}</p>
                              )}
                              <AttachmentList attachments={comment.attachments} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Comment - only if not resolved */}
                      {query.status !== 'resolved' && (
                        <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={queryCommentText}
                            onChange={(e) => setQueryCommentText(e.target.value)}
                            placeholder="Add a message..."
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddQueryComment();
                              }
                            }}
                          />
                          <button
                            onClick={handleAddQueryComment}
                            disabled={addingQueryComment || (!queryCommentText.trim() && queryCommentFiles.length === 0)}
                            className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
                          >
                            {addingQueryComment ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Send size={16} />
                            )}
                          </button>
                        </div>
                        <FilePicker files={queryCommentFiles} onChange={setQueryCommentFiles} disabled={addingQueryComment} />
                        </div>
                      )}
                      {query.status === 'resolved' && (
                        <p className="text-xs text-gray-500 text-center">This query is resolved. Reopen to continue the conversation.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Raise a Query Modal */}
      {showQueryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center">
                  <HelpCircle size={20} className="text-brand" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Raise a Query</h2>
              </div>
              <button
                onClick={resetQueryForm}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={queryCategoryId}
                  onChange={(e) => setQueryCategoryId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand focus:border-brand"
                >
                  <option value="">Select a category</option>
                  {queryCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={querySubject}
                  onChange={(e) => setQuerySubject(e.target.value)}
                  placeholder="e.g., Leads not showing in dashboard"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={queryMessage}
                  onChange={(e) => setQueryMessage(e.target.value)}
                  placeholder="Describe your query in detail..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand resize-none"
                />
              </div>

              <FilePicker files={queryFiles} onChange={setQueryFiles} disabled={submittingQuery} />
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={resetQueryForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitQuery}
                disabled={submittingQuery || !queryCategoryId || !querySubject.trim() || !queryMessage.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {submittingQuery ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Submit Query
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Query Modal */}
      {queryToReopen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <RotateCcw size={20} className="text-amber-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Reopen Query</h2>
              </div>
              <button
                onClick={() => {
                  setQueryToReopen(null);
                  setReopenComment('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Original Query</p>
                <p className="text-sm text-gray-900 font-medium">{queryToReopen.subject}</p>
                <p className="text-sm text-gray-600 mt-1">{queryToReopen.message}</p>
              </div>

              {queryToReopen.admin_notes && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs font-medium text-blue-700 mb-1">Previous Response:</p>
                  <p className="text-sm text-blue-800">{queryToReopen.admin_notes}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Why are you reopening this query?
                </label>
                <textarea
                  value={reopenComment}
                  onChange={(e) => setReopenComment(e.target.value)}
                  placeholder="Please explain what still needs to be addressed..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  setQueryToReopen(null);
                  setReopenComment('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReopenQuery}
                disabled={reopeningQuery || !reopenComment.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {reopeningQuery ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Reopening...
                  </>
                ) : (
                  <>
                    <RotateCcw size={16} />
                    Reopen Query
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deliverable Detail Modal */}
      {selectedDeliverable && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center border">
                  {getTypeIcon(selectedDeliverable.type)}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 line-clamp-1">{selectedDeliverable.title}</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    {getStatusBadge(selectedDeliverable.status)}
                    {selectedDeliverable.type === 'blog' && getBlogStatusBadge(selectedDeliverable)}
                  </div>
                </div>
              </div>
              <button
                onClick={async () => {
                  setSelectedDeliverable(null);
                  setExpandedDeliverable(null);
                  setDeliverableComments([]);
                  // Refresh deliverables to update unread counts
                  fetchDeliverables();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Details */}
              {selectedDeliverable.delivery_month && selectedDeliverable.delivery_year && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                  <Calendar size={14} />
                  <span>{months.find(m => m.value === selectedDeliverable.delivery_month)?.label} {selectedDeliverable.delivery_year}</span>
                </div>
              )}
              {selectedDeliverable.description && (
                <p className="text-sm text-gray-600 mb-4">{selectedDeliverable.description}</p>
              )}
              {selectedDeliverable.url && (
                <a
                  href={getContentUrl(selectedDeliverable)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-brand hover:underline mb-4"
                >
                  <ExternalLink size={14} />
                  View Content
                </a>
              )}

              {/* Approval Info */}
              {selectedDeliverable.status === 'approved' && selectedDeliverable.reviewer_name && (
                <p className="text-xs text-green-600 flex items-center gap-1 mb-4">
                  <CheckCircle size={12} />
                  Approved by {selectedDeliverable.reviewer_name.split('[')[0].trim()}
                </p>
              )}

              {/* Review Link */}
              {selectedDeliverable.review_token && selectedDeliverable.status === 'pending' && (
                <a
                  href={`${window.location.origin}/review/${selectedDeliverable.review_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors mb-4"
                >
                  <Eye size={14} />
                  Review & Approve
                </a>
              )}

              {/* Comments */}
              <div className="border-t pt-4 mt-2">
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <MessageSquare size={16} />
                  Comments
                </h4>
                {loadingComments ? (
                  <div className="flex justify-center py-4">
                    <Loader2 size={20} className="animate-spin text-gray-400" />
                  </div>
                ) : deliverableComments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
                ) : (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {deliverableComments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">{comment.user_name}</span>
                          <span className="text-xs text-gray-400">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{linkifyText(comment.comment)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Comment Input Footer */}
            <div className="border-t px-6 py-4 shrink-0">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <button
                  onClick={handleAddComment}
                  disabled={addingComment || !commentText.trim()}
                  className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
                >
                  {addingComment ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDeliverables;
